import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CreatePropertyPricingRule, Property } from '../../../../../features/property/models/property.model';
import { PropertyPricingRuleService } from '../../../../../features/property/services/property-pricing-rule.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { SnackBarService } from '../../../../services/snackbar.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { HapticsService } from '../../../../general/services/haptics.service';

@Component({
  selector: 'app-add-property-pricing-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatCheckboxModule,
    MatSliderModule,
    MatNativeDateModule,
    MatDialogModule,
    MatTooltipModule,
    MatButtonToggleModule
  ],
  templateUrl: './add-property-pricing-form.html',
  styleUrl: './add-property-pricing-form.scss',
})
export class AddPropertyPricingForm implements OnInit {
  private pricingService = inject(PropertyPricingRuleService);
  private auth = inject(AuthService);
  private haptics = inject(HapticsService);
  private snackbar = inject(SnackBarService);

  private fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<AddPropertyPricingForm>);
  data = inject<Property>(MAT_DIALOG_DATA);

  form: FormGroup;

  readonly days = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' },
  ];

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      priority: [5, [Validators.required, Validators.min(1), Validators.max(9)]],
      startDate: [new Date(), Validators.required],
      endDate: [new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), Validators.required],
      daysOfWeek: [[]],
      pricingType: ['fixed', Validators.required],
      price: [null, [Validators.min(0)]],
      priceMultiplier: [null, [Validators.min(0.1)]],
    }, { validators: this.priceOrMultiplierValidator });
  }
  
  ngOnInit() {
    console.log('Property data:', this.data);
  }

  toggleDay(dayValue: number, checked: boolean) {
    this.haptics.click();
    let days = this.form.get('daysOfWeek')?.value as number[] ?? [];
    if (checked) {
      if (!days.includes(dayValue)) {
        days = [...days, dayValue].sort((a, b) => a - b);
      }
    } else {
      days = days.filter(v => v !== dayValue);
    }
    this.form.patchValue({ daysOfWeek: days });
  }

  get selectedDaysLabel(): string {
    const daysArr = this.form.get('daysOfWeek')?.value as number[] ?? [];
    if (!daysArr.length) return '';
    return 'Selected: ' + daysArr
      .map(v => this.days.find(d => d.value === v)?.label ?? 'Unknown')
      .join(', ');
  }

  private priceOrMultiplierValidator(group: FormGroup): ValidationErrors | null {
    const type = group.get('pricingType')?.value;
    const price = group.get('price')?.value;
    const multiplier = group.get('priceMultiplier')?.value;

    if (type === 'fixed') {
      if (!price || price <= 0) {
        group.get('price')?.setErrors({ required: true });
        return { priceRequired: true };
      }
      group.get('priceMultiplier')?.setErrors(null);
    } else if (type === 'multiplier') {
      if (!multiplier || multiplier < 0.1) {
        group.get('priceMultiplier')?.setErrors({ required: true });
        return { multiplierRequired: true };
      }
      group.get('price')?.setErrors(null);
    }

    return null;
  }

  onPricingTypeChange(type: string) {
    this.haptics.click();
    if (type === 'fixed') {
      this.form.get('priceMultiplier')?.setValue(null);
      this.form.get('priceMultiplier')?.clearValidators();  // optional
    } else {
      this.form.get('price')?.setValue(null);
      this.form.get('price')?.clearValidators();  // optional
    }
    this.form.updateValueAndValidity();
  }

  onSave() {
    this.haptics.click();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackbar.open('Please fill required fields', 'Close', { duration: 4000 });
      return;
    }

    const value = this.form.value;
    const request: CreatePropertyPricingRule = {
      propertyId: this.data.id,
      name: value.name,
      priority: value.priority,
      startDate: value.startDate.toISOString(),
      endDate: value.endDate.toISOString(),
      daysOfWeek: value.daysOfWeek,
      priceCents: value.pricingType === 'fixed' ? Math.round(value.price * 100) : null,
      priceMultiplier: value.pricingType === 'multiplier' ? value.priceMultiplier : null,
      minNights: value.minNights ?? null,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.pricingService.createPricingRule(request).subscribe({
      next: () => {
        this.snackbar.open('Pricing rule created!', 'OK', { duration: 5000, panelClass: 'success-snackbar' });
        this.dialogRef.close(true);  // close with success flag if parent needs it
      },
      error: (err) => {
        console.error(err);
        this.snackbar.open('Error creating rule. Try again.', 'Close', { duration: 6000, panelClass: 'error-snackbar' });
      }
    });
  }

  onCancel() {
    this.haptics.click();
    this.dialogRef.close(false);
  }
}